import axios, { type InternalAxiosRequestConfig } from "axios";
import useTokenStore from "../store/tokenStore";
import useConfigStore from "../store/configStore";

// const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Queue to hold requests while waiting for config initialization
interface QueuedRequest {
  resolve: (value: InternalAxiosRequestConfig) => void;
  reject: (error: any) => void;
  config: InternalAxiosRequestConfig;
}

let requestQueue: QueuedRequest[] = [];
let isConfigReady = false;
let isWaitingForConfig = false;
let base = "";

// Function to check if config is initialized
const checkConfigInitialized = (): boolean => {
  const config = useConfigStore.getState().getConfig();
  if (config?.baseUrl && config.baseUrl.trim() !== "") {
    base = config.baseUrl;
    return true;
  }
  return false;
};

// Function to wait for config initialization with polling
const waitForConfigInitialization = async (maxWaitTime = 10000, checkInterval = 50): Promise<void> => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    const checkConfig = () => {
      if (checkConfigInitialized()) {
        isConfigReady = true;
        isWaitingForConfig = false;
        resolve();
        return;
      }
      
      // Check if we've exceeded max wait time
      if (Date.now() - startTime >= maxWaitTime) {
        // Fallback to uniBaseUrl if config still not available
        const fallbackUrl = useTokenStore.getState().uniBaseUrl;
        if (fallbackUrl && fallbackUrl.trim() !== "") {
          base = fallbackUrl;
          isConfigReady = true;
          isWaitingForConfig = false;
          console.warn(
            `Config not initialized after ${maxWaitTime}ms. Using fallback base URL: ${fallbackUrl}`
          );
          resolve();
        } else {
          isWaitingForConfig = false;
          const error = new Error(
            `Config not initialized after ${maxWaitTime}ms and no fallback URL available.`
          );
          console.error(error.message);
          reject(error);
        }
        return;
      }
      
      // Continue checking
      setTimeout(checkConfig, checkInterval);
    };
    
    // Start checking immediately
    checkConfig();
  });
};

// Start checking for config initialization immediately (non-blocking)
const initConfigCheck = async () => {
  if (isConfigReady) return;
  
  if (isWaitingForConfig) {
    // Already waiting, don't start another check
    return;
  }
  
  isWaitingForConfig = true;
  
  try {
    await waitForConfigInitialization();
    // Process queued requests once config is ready
    processQueuedRequests();
  } catch (error) {
    // Reject all queued requests if config initialization fails
    rejectQueuedRequests(error);
  }
};

// Process all queued requests once config is ready
const processQueuedRequests = () => {
  const queue = [...requestQueue];
  requestQueue = [];
  
  queue.forEach(({ resolve, config }) => {
    // Resolve with the config so the request interceptor can proceed
    resolve(config);
  });
};

// Reject all queued requests
const rejectQueuedRequests = (error: any) => {
  const queue = [...requestQueue];
  requestQueue = [];
  
  queue.forEach(({ reject }) => {
    reject(error);
  });
};

// Create axios instance with empty baseURL initially
// It will be updated once config is ready
const axiosInstance = axios.create({
  baseURL: "",
  headers: {
    "Content-Type": "application/json",
    // Timezone: timezone, 
    // Custom header
  },
});

// Start initialization check immediately
initConfigCheck().catch((error) => {
  console.error("Failed to initialize config:", error);
});

let isRefreshing = false;
// disable tslint
let failedQueue: any[] = [];
let retryCount = 0; // Track retry attempts
const MAX_RETRY_LIMIT = 3; // Define a maximum retry limit

const processQueue = (error: any, token = null) => {
  failedQueue.forEach((prom: any) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });
  failedQueue = [];
};

// Request interceptor - queues requests if config is not ready
axiosInstance.interceptors.request.use(
  async (config) => {
    // Check if config is ready
    if (!isConfigReady) {
      // Check one more time synchronously
      if (!checkConfigInitialized()) {
        // Config still not ready, queue this request
        return new Promise<InternalAxiosRequestConfig>((resolve, reject) => {
          requestQueue.push({
            resolve: () => resolve(config), // Resolve with the config so the request proceeds
            reject,
            config,
          });
          
          // Ensure we're waiting for config
          if (!isWaitingForConfig) {
            initConfigCheck().catch((error) => {
              reject(error);
            });
          }
        });
      } else {
        // Config just became ready, update baseURL
        isConfigReady = true;
        axiosInstance.defaults.baseURL = base;
      }
    }
    
    // Update baseURL from store on each request to ensure it's current
    const currentConfig = useConfigStore.getState().getConfig();
    if (currentConfig?.baseUrl) {
      config.baseURL = currentConfig.baseUrl;
      axiosInstance.defaults.baseURL = currentConfig.baseUrl;
    } else if (base) {
      config.baseURL = base;
    }
    
    const token = useTokenStore.getState().token;
    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
    
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;

    if (response?.status === 401 && !config._retry) {
      config._retry = true; // Prevent retrying the same request multiple times
      const originalRequest = config;

      if (retryCount >= MAX_RETRY_LIMIT) {
        console.error("Maximum retry limit reached. Logging out user.");
        useTokenStore.getState().setToken("");
        // window.location.href = "/auth"; // Redirect to login page
        window.location.href = "/login"; // Redirect to login page
        // return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject, originalRequest });
        });
      }

      isRefreshing = true;

      try {
        // Refresh the token
        const refreshToken = localStorage.getItem("refreshToken");
        // Get current base URL
        const currentBaseUrl = useConfigStore.getState().getBaseUrl() || base || "";
        const { data } = await axios.post(
          `${currentBaseUrl}/api/v1/auth/refresh-token`,
          {
            refreshToken: refreshToken || "",
          },
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );

        // Update token in store
        useTokenStore.getState().setToken(data.data);

        // Retry all failed requests
        failedQueue.forEach(({ resolve, originalRequest }: any) => {
          originalRequest.headers["Authorization"] = `Token ${data.data}`;
          resolve(axiosInstance(originalRequest));
        });

        failedQueue = []; // Clear the queue

        // Retry the original request
        originalRequest.headers["Authorization"] = `Token ${data.data}`;
        retryCount = 0; // Reset retry count on success
        return axiosInstance(originalRequest);
      } catch (refreshError: any) {
        // // If forbidden error, log the user out
        // if (refreshError?.response?.status === 403) {
        //   useTokenStore.getState().setToken("");
        // }
        processQueue(refreshError, null);
        retryCount++; // Increment retry count on failure
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
